import { useEffect, useState } from 'react';

/**
 * Subscribes to a CSS media query and returns whether it currently matches. UI-only
 * (a resize/media listener, not data fetching); re-renders only when the match flips.
 *
 * @param {string} query - A media query string, e.g. `(max-width: 1499px)`.
 * @return {boolean} True while the query currently matches.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    // Sync once in case it changed between the initial render and this effect.
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
